import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Menu, X } from "lucide-react";
import { GlitchButton } from "../ui/GlitchButton";
import gcLogo from "../../assets/gclogo2026.png";
import { BOOKING_URL, BRAND } from "../../lib/brand";

const NAV_ITEMS = [
  { label: "How to hire me", section: "engagement" },
  { label: "What I am proud of", section: "projects" },
  { label: "Capabilities", section: "skills" },
  { label: "References", section: "testimonials" },
] as const;

export function ProfileNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isProfileHome = location.pathname === "/olga";
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const goToSection = (sectionId: string) => {
    setIsOpen(false);
    const scroll = () => document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (isProfileHome) {
      scroll();
    } else {
      navigate(`/olga#${sectionId}`);
      setTimeout(scroll, 100);
    }
  };

  return (
    <nav
      className="fixed left-0 top-0 w-full z-50"
      style={{
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(8.5px)",
        WebkitBackdropFilter: "blur(8.5px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
      }}
    >
      <div className="flex items-center justify-between px-4 lg:px-[116px] py-4 lg:py-[20px] max-w-[1920px] mx-auto">
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => goToSection("landing")}
            className="shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414] rounded-lg"
            aria-label="Olga Molina — back to top"
          >
            <img src={gcLogo} alt="Olga Molina — Geekcow" className="h-[38px] lg:h-[52px] w-auto" />
          </button>

          <div className="hidden lg:flex items-center gap-3">
            {NAV_ITEMS.map((item) => (
              <GlitchButton
                key={item.section}
                text={item.label}
                onClick={() => goToSection(item.section)}
                className="h-[44px] px-5 font-medium text-[16px]"
              />
            ))}
          </div>
        </div>

        {/* Mobile trigger */}
        <div className="lg:hidden">
          <button className="p-2 text-[#141414]" onClick={() => setIsOpen(true)} aria-label="Open menu">
            <Menu size={30} strokeWidth={2.5} />
          </button>

          <AnimatePresence>
            {isOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40"
                  onClick={() => setIsOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, x: "-50%", y: -10 }}
                  animate={{ opacity: 1, scale: 1, x: "-50%", y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, x: "-50%", y: -10 }}
                  className="fixed left-1/2 top-[92px] w-[90%] max-w-[320px] bg-white/85 backdrop-blur-xl border border-white/40 rounded-[32px] p-6 z-50 shadow-2xl flex flex-col items-center gap-3"
                >
                  <button
                    onClick={() => setIsOpen(false)}
                    className="self-end -mt-2 -mr-1 p-1 text-[#141414]"
                    aria-label="Close menu"
                  >
                    <X size={22} strokeWidth={2.5} />
                  </button>

                  {NAV_ITEMS.map((item) => (
                    <button
                      key={item.section}
                      onClick={() => goToSection(item.section)}
                      className="w-full py-3 rounded-full bg-white border border-[#E5E5E5] text-[#141414] text-[17px] font-medium hover:bg-gray-50 transition-colors"
                    >
                      {item.label}
                    </button>
                  ))}

                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-4 rounded-full text-center text-[#003e5b] font-bold text-[13px] tracking-[2px] uppercase hover:scale-105 transition-transform mt-1"
                    style={{ background: BRAND.greenBright }}
                  >
                    Book a call
                  </a>

                  <Link
                    to="/"
                    onClick={() => setIsOpen(false)}
                    className="w-full py-3 rounded-full border border-[#141414] text-center text-[14px] font-medium text-[#141414] hover:bg-[#141414] hover:text-white transition-colors"
                  >
                    Do you need more than a couple of hands?
                  </Link>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop right side */}
        <div className="hidden lg:flex items-center gap-5">
          <Link
            to="/"
            className="hidden xl:block text-[13px] font-medium text-[#003e5b] underline-offset-4 hover:underline whitespace-nowrap"
          >
            Need more than a couple of hands?
          </Link>

          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="h-[52px] px-8 rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-lg"
            style={{ background: BRAND.greenBright }}
          >
            <span className="font-bold text-[12.6px] tracking-[2.1px] uppercase text-[#003e5b]">Book a call</span>
          </a>
        </div>
      </div>
    </nav>
  );
}
